import React, { useRef, useState } from 'react';
import BirthdayField from './custom-form/BirthdayField';
import { parseBirthday, type BirthdayParts } from './custom-form/birthday';
import { getLocalToday } from '../utils/date';
import { formatPhone } from './PhoneStep';

interface Props {
  onSubmit: (phone: string, dob: string) => Promise<void>;
  error: string | null;
  onEdit: () => void;
}

export default function LinkReissueStep({ onSubmit, error, onEdit }: Props) {
  const [phone, setPhone] = useState('');
  const [parts, setParts] = useState<BirthdayParts>({ month: '', day: '', year: '' });
  const [localError, setLocalError] = useState<string | null>(null);
  const activeError = localError ?? error;
  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const digits = phone.replace(/\D/g, '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting.current || digits.length !== 10) return;
    const result = parseBirthday(parts, getLocalToday());
    if (result.status !== 'valid') {
      const messages = {
        empty: '"Date of birth" is required.',
        partial: 'Enter the month, day and year for "Date of birth".',
        invalid: '"Date of birth" must be a real date.',
        future: `"Date of birth" can't be in the future.`,
      };
      setLocalError(messages[result.status]);
      return;
    }
    submitting.current = true;
    setPending(true);
    try { await onSubmit(digits, result.iso); }
    finally { submitting.current = false; setPending(false); }
  }

  return (
    <div className="py-4 max-w-md mx-auto">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center">Text me a new link</h2>
      <p className="text-gray-500 mt-3 text-center">Enter your mobile phone number and date of birth to request a new text.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label htmlFor="reissue-phone" className="block text-sm font-semibold text-gray-700">Mobile phone number</label>
          <input id="reissue-phone" type="tel" autoComplete="tel" inputMode="numeric" required
            className="input-field mt-2" value={phone} disabled={pending}
            onChange={(e) => { setPhone(formatPhone(e.target.value)); onEdit(); }} />
        </div>
        <div>
          <BirthdayField
            field={{ label: 'Date of birth', required: false }}
            inputId="reissue-dob"
            parts={parts}
            onPartChange={(part, value) => {
              setParts((current) => ({ ...current, [part]: value }));
              setLocalError(null);
              onEdit();
            }}
            disabled={pending}
            errorId={activeError ? 'reissue-dob-error' : undefined}
            className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 focus:border-primary-deep focus:ring-1 focus:ring-primary-deep outline-none"
          />
        </div>
        {activeError && <p id="reissue-dob-error" role="alert" className="text-sm font-medium text-red-600">{activeError}</p>}
        <button type="submit" disabled={digits.length !== 10 || pending}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">
          {pending ? 'Requesting…' : 'Text me a new link'}
        </button>
      </form>
    </div>
  );
}
